import { useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type {
  Block,
  BlockLevel,
  CreateSubBlockInput,
  CreateTopBlockInput,
  MeResponse,
  Pillar
} from "../../api/types";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { levelLabel } from "./BlockCard";
import { creatableLevels, parentCandidates, parentOptionLabel } from "./createBlockOptions";

const scores = [1, 2, 3, 4, 5];

// CreateBlockForm creates a block at any level from All Blocks. A Project lands
// in the season the page shows (seasonId "" = active); a Block or Sub-block
// hangs off a parent picked from the page's loaded blocks, so it inherits that
// season. An Execution request is queued for approval rather than created, so
// the outcome is reported, never assumed.
export function CreateBlockForm({
  me,
  seasonId,
  blocks,
  pillars,
  onCreated
}: {
  me: MeResponse;
  seasonId: string;
  blocks: Block[];
  pillars: Pillar[];
  onCreated: () => void;
}) {
  const levels = creatableLevels(me);
  const [level, setLevel] = useState<BlockLevel>(levels[0] ?? "ic");
  const [parentId, setParentId] = useState("");
  const [name, setName] = useState("");
  const [pillarId, setPillarId] = useState("");
  const [impact, setImpact] = useState(3);
  const [significance, setSignificance] = useState(3);
  const [size, setSize] = useState(0);
  const [team, setTeam] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { keyFor, reset } = useIdempotencyKey();

  const activePillars = pillars.filter((pillar) => !pillar.archived);
  const effectivePillarId = pillarId !== "" ? pillarId : (activePillars[0]?.id ?? "");
  const parents = parentCandidates(blocks, level, me);
  const blocksById = new Map(blocks.map((block) => [block.id, block]));
  const effectiveParentId = parents.some((parent) => parent.id === parentId) ? parentId : (parents[0]?.id ?? "");
  const ready =
    name.trim() !== "" && dueDate !== "" && (level === "top" ? effectivePillarId !== "" : effectiveParentId !== "");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setNotice(null);
    const trimmedName = name.trim();
    const optional = {
      ...(team.trim() === "" ? {} : { team: team.trim() }),
      ...(acceptanceCriteria.trim() === "" ? {} : { acceptance_criteria: acceptanceCriteria.trim() })
    };
    try {
      if (level === "top") {
        const payload: CreateTopBlockInput = {
          name: trimmedName,
          pillar_id: effectivePillarId,
          impact,
          significance,
          due_date: dueDate,
          ...optional,
          ...(seasonId === "" ? {} : { season_id: seasonId })
        };
        await apiClient.createTopBlock(payload, keyFor(JSON.stringify(payload)));
        setNotice(`Created “${trimmedName}”.`);
        onCreated();
      } else {
        const payload: CreateSubBlockInput = { level, name: trimmedName, size, due_date: dueDate, ...optional };
        const result = await apiClient.createSubBlock(
          effectiveParentId,
          payload,
          keyFor(`${effectiveParentId}:${JSON.stringify(payload)}`)
        );
        if ("approval" in result) {
          setNotice(`Submitted “${trimmedName}” for approval.`);
        } else {
          setNotice(`Created “${trimmedName}”.`);
          onCreated();
        }
      }
      setName("");
      setSize(0);
      setAcceptanceCriteria("");
      reset();
    } catch (caught) {
      // The server replays a stored answer for a reused key, so a refused
      // request needs a new key to be retried; a network error may never have
      // reached the server, so that retry keeps the key.
      if (caught instanceof ApiError) {
        reset();
      }
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="form-row">
      <h3>New block</h3>
      <div className="form-row form-row--inline">
        <label>
          Level
          <select value={level} onChange={(event) => setLevel(event.target.value as BlockLevel)}>
            {levels.map((option) => (
              <option key={option} value={option}>
                {levelLabel(option)}
              </option>
            ))}
          </select>
        </label>
        {level !== "top" && (
          <label className="parent-picker">
            Parent {levelLabel(level === "mid" ? "top" : "mid").toLowerCase()}
            <select value={effectiveParentId} onChange={(event) => setParentId(event.target.value)}>
              {parents.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parentOptionLabel(parent, blocksById)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Name
          <input
            type="text"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        {level === "top" && (
          <>
            <label>
              Pillar
              <select value={effectivePillarId} onChange={(event) => setPillarId(event.target.value)}>
                {activePillars.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Impact
              <select value={impact} onChange={(event) => setImpact(Number(event.target.value))}>
                {scores.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Significance
              <select value={significance} onChange={(event) => setSignificance(Number(event.target.value))}>
                {scores.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        {level !== "top" && (
          <label>
            Size
            <input
              type="number"
              min={0}
              value={size}
              onChange={(event) => setSize(Math.max(0, Number(event.target.value)))}
            />
          </label>
        )}
        <label>
          Team
          <input type="text" value={team} onChange={(event) => setTeam(event.target.value)} placeholder="optional" />
        </label>
        <label>
          Due date
          <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} required />
        </label>
      </div>
      {level !== "top" && parents.length === 0 && (
        <p className="muted">
          {level === "mid" ? "No projects in this season yet." : "No blocks you can add a sub-block to in this season."}
        </p>
      )}
      <label>
        Acceptance criteria
        <textarea
          value={acceptanceCriteria}
          rows={3}
          onChange={(event) => setAcceptanceCriteria(event.target.value)}
          placeholder="optional"
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={!ready || submitting}>
        {submitting ? "Creating…" : `Create ${levelLabel(level).toLowerCase()}`}
      </button>
      {error !== null && <p className="form-row__error">{error}</p>}
      {notice !== null && (
        <p className="form-row__success" role="status">
          {notice}
        </p>
      )}
    </form>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
